import axios from "axios";
import { defineStore } from "pinia";
import { useConversation } from "./Conversation";
import DateUtilities from "../Utilities/DateUtilities";
import { useAppState } from "./AppState";
import { useUserStore } from "./User";
import { handleInviteApi } from "../Helpers/chin";
import { useWorkspace } from "./Workspaces";

export const useActivity = defineStore("ActivityStore", {
    state: () => ({
        selected_list: "",
        hovered_list: "",
        expanded_list: false,
        profile_ids_conversed_within_selected_days: [],
        filters: {
            profile_id: null,
            space_id: null,
            board_id: null,
            days: null,
            conversation_types: [],
        },
        selected_activity: null,
        documentDetails: {
            open: false,
            conv_id: undefined,
        },
    }),
    getters: {},
    actions: {
        setSelectedList(value) {
            if (!value) return;

            this.selected_list = value;
        },
        setHoveredList(value) {
            if (!value) return;

            this.hovered_list = value;
        },
        toggleExpand(value = null) {
            if (value === null) {
                return (this.expanded_list = !this.expanded_list);
            }

            this.expanded_list = value;
        },
        setSelectedUserId(val) {
            this.filters.profile_id = val;
        },
        setSelectedSpaceId(val) {
            this.filters.space_id = val;
        },
        setSelectedBoardId(val) {
            this.filters.board_id = val;
        },
        setSelectedDays(val) {
            this.filters.days = val;
            this.prepareProfilesConversedWithinSelectedDays();
        },
        setSelectedConversationType(val) {
            this.filters.conversation_types = val;
        },
        prepareProfilesConversedWithinSelectedDays() {
            const profiles_conversed_within_selected_days = new Set();

            let conversations = useConversation().handlers;
            if (this.filters.days) {
                conversations = conversations.filter((conv) => {
                    const last_message_date =
                        conv?.getTwilioConversation()?.lastMessage?.dateCreated;
                    return last_message_date
                        ? DateUtilities.getDifferenceInDays(
                              last_message_date,
                              new Date(),
                          ) <= this.filters.days
                        : "";
                });
            }

            const conv_profiles_ids = conversations
                .filter((conv) => !conv.isGroup())
                .map((conv) => conv.getActiveParticipantIds());

            const userStore = useUserStore();

            conv_profiles_ids?.forEach((conv) => {
                const sort_latest_profile = conv.reverse();
                sort_latest_profile.forEach((user_id) => {
                    const profile = userStore.getUserProfileById(user_id);

                    if (profile?.is_agent) {
                        const actualUserProfile =
                            userStore.getAgentUserProfileByAgentId(user_id);
                        if (
                            actualUserProfile?.id ===
                            userStore?.user_data.support_admin?.id
                        )
                            return;
                    }

                    return profiles_conversed_within_selected_days.add(user_id);
                });
            });

            this.profile_ids_conversed_within_selected_days =
                profiles_conversed_within_selected_days;
        },
        filterConversations() {
            let conversations = useConversation().handlers;
            if (this.filters.space_id)
                conversations = conversations.filter(
                    (item) => item.space_id === this.filters.space_id,
                );

            if (this.filters.board_id)
                conversations = conversations.filter(
                    (item) => item.board_id === this.filters.board_id,
                );

            if (this.filters.conversation_types.length > 0)
                conversations = this.filterConversationsByTypes(conversations);

            const seenIds = new Set();
            conversations = conversations.filter((conv) => {
                const convId = conv.id || conv.sid;
                if (seenIds.has(convId) || conv.isFake()) {
                    return false;
                }
                seenIds.add(convId);
                return true;
            });

            this.prepareProfilesConversedWithinSelectedDays();

            if (this.filters.profile_id)
                conversations = this.filterConversationsWithUser(conversations);

            // Remove duplicate conversations by ID

            return conversations;
        },
        filterConversationsWithLastMessageInDays(conversations) {
            conversations = conversations.filter((item) => {
                const last_message_date =
                    item?.getTwilioConversation()?.lastMessage?.dateCreated;
                return last_message_date
                    ? DateUtilities.getDifferenceInDays(
                          last_message_date,
                          new Date(),
                      ) <= this.filters.days
                    : "";
            });
            return conversations;
        },

        filterConversationsWithUser(conversations) {
            return conversations.filter((conv) =>
                conv.participants.find(
                    (user) =>
                        user.is_removed == false &&
                        user.twilio_identity == this.filters.profile_id,
                ),
            );
        },

        filterConversationsByTypes(conversations) {
            return conversations.filter((conversation) => {
                if (
                    this.filters.conversation_types.includes("dm") &&
                    conversation.isDm()
                )
                    return true;

                if (
                    this.filters.conversation_types.includes("group") &&
                    conversation.isGroup() &&
                    !conversation?.state?.is_one_off
                )
                    return true;

                if (
                    this.filters.conversation_types.includes("task") &&
                    conversation.isItem()
                )
                    return true;

                if (
                    this.filters.conversation_types.includes("board") &&
                    conversation.isBoard()
                )
                    return true;

                if (
                    this.filters.conversation_types.includes("topics") &&
                    conversation.isGroup() &&
                    conversation?.state?.is_one_off
                ) {
                    return true;
                }
                return false;
            });
        },

        setSelectedActivity(val) {
            // if (val.id === this.selected_activity?.id)
            //     this.selected_activity = null
            // else
            this.selected_activity = val;
        },

        toggleDocument(show = false, convId = undefined) {
            this.documentDetails = {
                open: show,
                conv_id: show ? convId : undefined,
            };
            if (show) {
                useAppState().openDocumentInActivity();
            } else {
                useAppState().closeDocumentInActivity();
            }
        },
        handleInvitationApiForItems() {
            const activity = this.selected_activity;

            if (!activity) {
                return;
            }

            if (
                activity.is_group_one_off ||
                activity.is_group ||
                activity.is_dm
            ) {
                return;
            }

            const activityData = activity.is_item
                ? activity.originalData?.item
                : {
                      entity_type: activity.type,
                      type_id: activity.type_id,
                      id: activity.id,
                      type: activity.type,
                  };

            if (activityData) {
                handleInviteApi(
                    {
                        selected_entities: [activityData],
                        update_store:
                            useWorkspace().getChinProfileIdsByEntityType(
                                activity.type,
                            ),
                    },
                    true,
                );
            }
        },
        updateSelectedActivityName(item) {
            if (!this.selected_activity) return;

            if (
                this.selected_activity.is_item &&
                this.selected_activity.originalData?.item?.id === item.id
            ) {
                this.selected_activity.originalData.item.name = item.name;
                this.selected_activity.name = item.name;
            }
        },
    },
});
